import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { MuiMaterials } from "../@index.js";
import * as global from "../data/global";
import { createApiEndpoint } from "../index.js";
import {
    ModalChrome,
    ModalBody,
    ModalContentTile,
    SITE_WINDOW_IDS,
    ALL_SITES_OPTION,
    outlinedActionButtonSx,
    containedActionButtonSx,
    CenteredLoadingOverlay,
    openSiteWindow,
    ConfirmDialog,
} from "./windowChrome/index.js";

const POLL_MS = 15000;

const cableNameForScanRow = (row) => {
    if (row?.scanCable == null) return "";
    const want = global.normalizeGuidForCompare?.(row.customerSiteGuid)
        ?? String(row.customerSiteGuid || "").toLowerCase();
    const site = (global.gw.siteInfos || []).find((s) => {
        const have = global.normalizeGuidForCompare?.(s.siteGuid)
            ?? String(s.siteGuid || "").toLowerCase();
        return have === want;
    });
    const cables = Array.isArray(site?.cables) ? site.cables : [];
    const id = Number(row.scanCable);
    const match = cables.find((c) => Number(c.id ?? c.cableId) === id);
    return String(match?.name ?? match?.cableName ?? "").trim();
};

const formatScanCable = (row) => {
    const name = cableNameForScanRow(row);
    if (name) return name;
    return row?.scanCable != null ? String(row.scanCable) : "—";
};

const formatStarted = (dateStr) => {
    if (!dateStr) return "—";
    try {
        return new Date(dateStr).toLocaleString(
            "en-us",
            global.dateFormat || {
                month: "2-digit",
                day: "2-digit",
                year: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            },
        );
    } catch {
        return String(dateStr);
    }
};

const SitesScanningModal = ({ isModalOpen, setIsModalOpen, filterToSiteGuid }) => {
    const [currentTheme, setCurrentTheme] = useState(global.gw.theme || "dark");
    const [forceUpdate, setForceUpdate] = useState(0);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [message, setMessage] = useState(null);
    const [selectedSiteGuid, setSelectedSiteGuid] = useState(ALL_SITES_OPTION);
    const [searchText, setSearchText] = useState("");
    const [sortBy, setSortBy] = useState("dateStarted");
    const [sortOrder, setSortOrder] = useState("desc");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const getThemeColor = useCallback((lightColor, darkColor) => {
        return currentTheme === "light" ? lightColor : darkColor;
    }, [currentTheme]);

    const themeText = getThemeColor("#333333", "#f4cc8e");
    const themeBg = getThemeColor("#ffffff", "#2d2d2d");
    const headerBg = getThemeColor("#f5f5f5", "#3d3d3d");
    const muted = getThemeColor("#666666", "#a8abb1");

    const modalTheme = useMemo(() => {
        const isLight = currentTheme === "light";
        const text = isLight ? "#333333" : "#f4cc8e";
        const bg = isLight ? "#ffffff" : "#2d2d2d";
        return MuiMaterials.createTheme({
            palette: {
                mode: isLight ? "light" : "dark",
                text: { primary: text, secondary: text },
            },
            components: {
                MuiTableCell: {
                    styleOverrides: {
                        root: { color: text },
                    },
                },
                MuiTableSortLabel: {
                    styleOverrides: {
                        root: {
                            color: `${text} !important`,
                            "&:hover": { color: `${text} !important` },
                            "& .MuiTableSortLabel-icon": { color: `${text} !important` },
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            color: text,
                            backgroundColor: bg,
                        },
                        input: {
                            color: text,
                            WebkitTextFillColor: text,
                        },
                    },
                },
            },
        });
    }, [currentTheme, forceUpdate]);

    useEffect(() => {
        const handleThemeChange = () => {
            setCurrentTheme(global.gw.theme || "dark");
            setForceUpdate((prev) => prev + 1);
        };
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);

    useEffect(() => {
        if (!isModalOpen[0]) return;
        setSelectedSiteGuid(filterToSiteGuid || ALL_SITES_OPTION);
        setSearchText("");
    }, [isModalOpen[0], filterToSiteGuid]);

    const loadRows = useCallback(async ({ silent = false } = {}) => {
        if (!global.gw.auth || !global.gw.email) return;
        if (!silent) setLoading(true);
        try {
            const res = await createApiEndpoint().fetchSitesCurrentlyScanning("direct");
            const list =
                res?.data?.fetchSitesCurrentlyScanning
                ?? res?.fetchSitesCurrentlyScanning
                ?? [];
            setRows(Array.isArray(list) ? list : []);
            setMessage(null);
        } catch {
            if (!silent) {
                setRows([]);
                setMessage({ type: "error", text: "Failed to load sites that are currently scanning." });
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isModalOpen[0]) return undefined;
        loadRows();
        const id = setInterval(() => loadRows({ silent: true }), POLL_MS);
        return () => clearInterval(id);
    }, [isModalOpen[0], loadRows]);

    const isAllSites = selectedSiteGuid === ALL_SITES_OPTION || !selectedSiteGuid;

    const filteredRows = useMemo(() => {
        const want = isAllSites
            ? null
            : (global.normalizeGuidForCompare?.(selectedSiteGuid) ?? String(selectedSiteGuid).toLowerCase());
        const q = searchText.trim().toLowerCase();
        return rows.filter((row) => {
            if (want) {
                const have = global.normalizeGuidForCompare?.(row.customerSiteGuid)
                    ?? String(row.customerSiteGuid || "").toLowerCase();
                if (have !== want) return false;
            }
            if (!q) return true;
            const hay = [
                row.siteName,
                row.type,
                row.version,
                formatScanCable(row),
                row.scanCable,
                row.id,
            ].map((v) => String(v ?? "").toLowerCase()).join(" ");
            return hay.includes(q);
        });
    }, [rows, selectedSiteGuid, isAllSites, searchText]);

    const sortedRows = useMemo(() => {
        const arr = [...filteredRows];
        const mult = sortOrder === "asc" ? 1 : -1;
        arr.sort((a, b) => {
            if (sortBy === "site") {
                return mult * String(a.siteName || "").localeCompare(String(b.siteName || ""), undefined, { sensitivity: "base" });
            }
            if (sortBy === "type") {
                return mult * String(a.type || "").localeCompare(String(b.type || ""), undefined, { sensitivity: "base" });
            }
            if (sortBy === "version") {
                return mult * String(a.version || "").localeCompare(String(b.version || ""), undefined, { numeric: true, sensitivity: "base" });
            }
            if (sortBy === "progress") {
                const ap = (Number(a.requestedCount) || 0) === 0
                    ? 0
                    : (Number(a.finishedCount) || 0) / Number(a.requestedCount);
                const bp = (Number(b.requestedCount) || 0) === 0
                    ? 0
                    : (Number(b.finishedCount) || 0) / Number(b.requestedCount);
                return mult * (ap - bp);
            }
            const at = a.dateStarted ? new Date(a.dateStarted).getTime() : 0;
            const bt = b.dateStarted ? new Date(b.dateStarted).getTime() : 0;
            return mult * (at - bt);
        });
        return arr;
    }, [filteredRows, sortBy, sortOrder]);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(column);
            setSortOrder(column === "dateStarted" || column === "progress" ? "desc" : "asc");
        }
    };

    const resolveCatalogSiteGuid = (customerSiteGuid) => {
        if (!customerSiteGuid) return customerSiteGuid;
        const want = global.normalizeGuidForCompare?.(customerSiteGuid)
            ?? String(customerSiteGuid).toLowerCase();
        const match = (global.gw.siteInfos || []).find((s) => {
            const have = global.normalizeGuidForCompare?.(s?.siteGuid)
                ?? String(s?.siteGuid || "").toLowerCase();
            return have === want;
        });
        return match?.siteGuid || customerSiteGuid;
    };

    const openScan = (siteGuid) => {
        if (!siteGuid) return;
        setIsModalOpen([false]);
        openSiteWindow(SITE_WINDOW_IDS.scan, siteGuid);
    };

    const handleScanRowActivate = (row) => {
        const siteGuid = row?.customerSiteGuid;
        if (!siteGuid) return;
        if (isAllSites) {
            const guid = resolveCatalogSiteGuid(siteGuid);
            setSelectedSiteGuid(guid);
            openSiteWindow(SITE_WINDOW_IDS.sitesScanning, guid);
            return;
        }
        openScan(siteGuid);
    };

    const requestDeleteScan = (row, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        if (!row?.customerSiteGuid || row.id == null) return;
        setMessage(null);
        setDeleteTarget(row);
    };

    const closeDeleteConfirm = () => {
        if (deleting) return;
        setDeleteTarget(null);
    };

    const confirmDeleteScan = async () => {
        const row = deleteTarget;
        if (!row?.customerSiteGuid || row.id == null) {
            setDeleteTarget(null);
            return;
        }
        setDeleting(true);
        setMessage(null);
        try {
            const ok = await createApiEndpoint().deleteCustomerSiteDirectScan(
                "direct",
                row.customerSiteGuid,
                row.id,
            );
            if (!ok) {
                setMessage({ type: "error", text: "Failed to delete that scan." });
                setDeleteTarget(null);
                return;
            }
            setRows((prev) => prev.filter((r) =>
                !(String(r.customerSiteGuid) === String(row.customerSiteGuid) && Number(r.id) === Number(row.id)),
            ));
            setDeleteTarget(null);
        } catch {
            setMessage({ type: "error", text: "Failed to delete that scan." });
            setDeleteTarget(null);
        } finally {
            setDeleting(false);
        }
    };

    const sortLabelSx = {
        color: `${themeText} !important`,
        "&:hover": { color: `${themeText} !important` },
        "& .MuiTableSortLabel-icon": { color: `${themeText} !important` },
    };

    const headerCellSx = {
        fontWeight: 600,
        background: headerBg,
        color: themeText,
        whiteSpace: "nowrap",
    };

    return (
        <MuiMaterials.ThemeProvider theme={modalTheme}>
            <ModalChrome
                open={isModalOpen[0]}
                onClose={() => setIsModalOpen([false])}
                title="Sites Scanning"
                toolTitle="Sites Scanning"
                siteWindowId={SITE_WINDOW_IDS.sitesScanning}
                siteGuid={selectedSiteGuid || ALL_SITES_OPTION}
                showHeaderSiteSelect
                onHeaderSiteChange={(guid) => setSelectedSiteGuid(guid || ALL_SITES_OPTION)}
                headerFilterSite={(s) => !s.isHms}
                headerIncludeAllSites
                actions={(
                    <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, minWidth: 0 }}>
                        <MuiMaterials.Tooltip
                            arrow
                            placement="bottom"
                            title="Open DirectScan rows that are not complete. Type is Grade, BTS AutoScan, BTS Scan, TS AutoScan, TS Scan, or GW Scan. With All Sites selected, click a row to open Sites Scanning for that site. With one site selected, click a row to open that site's Scan window. Delete removes that scan from the list. The list refreshes every 15 seconds."
                            componentsProps={{
                                tooltip: {
                                    sx: { maxWidth: 360, fontSize: 12, lineHeight: 1.4 },
                                },
                            }}
                        >
                            <MuiMaterials.IconButton
                                size="small"
                                aria-label="Sites Scanning help"
                                sx={{ color: themeText, flexShrink: 0 }}
                            >
                                <HelpOutlineIcon fontSize="small" />
                            </MuiMaterials.IconButton>
                        </MuiMaterials.Tooltip>
                        <MuiMaterials.TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search site, type…"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={{ width: { xs: 140, sm: 200, md: 260 }, flexShrink: 1 }}
                        />
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            disabled={loading}
                            onClick={() => loadRows()}
                            sx={{ whiteSpace: "nowrap", flexShrink: 0, ...outlinedActionButtonSx() }}
                        >
                            Refresh
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                )}
                className="siteFilterModal hide-scrollbar"
                loadingOverlay={loading ? <CenteredLoadingOverlay /> : null}
            >
                <ModalBody className="siteFilterModalContent hide-scrollbar" sx={{ overflowY: "auto" }}>
                    <ModalContentTile sx={{ p: 0 }}>
                        {message?.text ? (
                            <MuiMaterials.Typography sx={{
                                padding: "12px 16px",
                                textAlign: "center",
                                color: message.type === "error" ? "#f44336" : themeText,
                            }}>
                                {message.text}
                            </MuiMaterials.Typography>
                        ) : null}
                        {loading && rows.length === 0 ? null : sortedRows.length === 0 ? (
                            message?.text ? null : (
                            <MuiMaterials.Typography sx={{ padding: "24px", textAlign: "center", color: muted }}>
                                {rows.length === 0
                                    ? "No sites are currently scanning."
                                    : "No matching scans for this filter."}
                            </MuiMaterials.Typography>
                            )
                        ) : (
                            <MuiMaterials.TableContainer sx={{ flex: 1, maxHeight: "100%", overflow: "auto" }}>
                                <MuiMaterials.Table stickyHeader size="small">
                                    <MuiMaterials.TableHead>
                                        <MuiMaterials.TableRow>
                                            <MuiMaterials.TableCell sx={headerCellSx}>
                                                <MuiMaterials.TableSortLabel active={sortBy === "site"} direction={sortOrder} onClick={() => handleSort("site")} sx={sortLabelSx}>
                                                    Site
                                                </MuiMaterials.TableSortLabel>
                                            </MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={headerCellSx}>
                                                <MuiMaterials.TableSortLabel active={sortBy === "type"} direction={sortOrder} onClick={() => handleSort("type")} sx={sortLabelSx}>
                                                    Type
                                                </MuiMaterials.TableSortLabel>
                                            </MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={headerCellSx}>
                                                <MuiMaterials.TableSortLabel active={sortBy === "dateStarted"} direction={sortOrder} onClick={() => handleSort("dateStarted")} sx={sortLabelSx}>
                                                    Started
                                                </MuiMaterials.TableSortLabel>
                                            </MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={headerCellSx}>
                                                <MuiMaterials.TableSortLabel active={sortBy === "progress"} direction={sortOrder} onClick={() => handleSort("progress")} sx={sortLabelSx}>
                                                    Progress
                                                </MuiMaterials.TableSortLabel>
                                            </MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={headerCellSx}>Cable</MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={headerCellSx}>
                                                <MuiMaterials.TableSortLabel active={sortBy === "version"} direction={sortOrder} onClick={() => handleSort("version")} sx={sortLabelSx}>
                                                    Version
                                                </MuiMaterials.TableSortLabel>
                                            </MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell sx={headerCellSx} align="right">Delete</MuiMaterials.TableCell>
                                        </MuiMaterials.TableRow>
                                    </MuiMaterials.TableHead>
                                    <MuiMaterials.TableBody>
                                        {sortedRows.map((row) => {
                                            const requested = Number(row.requestedCount) || 0;
                                            const finished = Number(row.finishedCount) || 0;
                                            const pct = requested > 0 ? Math.min(100, Math.round((finished / requested) * 100)) : 0;
                                            return (
                                                <MuiMaterials.TableRow
                                                    key={`${row.customerSiteGuid}-${row.id}`}
                                                    hover
                                                    onClick={() => handleScanRowActivate(row)}
                                                    sx={{
                                                        cursor: "pointer",
                                                        "&:nth-of-type(odd)": {
                                                            background: getThemeColor("rgba(0,0,0,0.02)", "rgba(255,255,255,0.02)"),
                                                        },
                                                    }}
                                                >
                                                    <MuiMaterials.TableCell sx={{ color: themeText, fontSize: "14px", fontWeight: 600 }}>
                                                        {row.siteName || "—"}
                                                    </MuiMaterials.TableCell>
                                                    <MuiMaterials.TableCell sx={{ color: themeText, fontSize: "14px", whiteSpace: "nowrap" }}>
                                                        {row.type || "—"}
                                                    </MuiMaterials.TableCell>
                                                    <MuiMaterials.TableCell sx={{ color: themeText, fontSize: "13px", whiteSpace: "nowrap" }}>
                                                        {formatStarted(row.dateStarted)}
                                                    </MuiMaterials.TableCell>
                                                    <MuiMaterials.TableCell sx={{ color: themeText, fontSize: "13px", minWidth: 140 }}>
                                                        <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <MuiMaterials.Box
                                                                sx={{
                                                                    flex: 1,
                                                                    height: 8,
                                                                    borderRadius: 4,
                                                                    overflow: "hidden",
                                                                    backgroundColor: getThemeColor("#e0e0e0", "rgba(244,204,142,0.2)"),
                                                                }}
                                                            >
                                                                <MuiMaterials.Box
                                                                    sx={{
                                                                        width: `${requested > 0 ? pct : 0}%`,
                                                                        height: "100%",
                                                                        backgroundColor: getThemeColor("#2e7d32", "#f4cc8e"),
                                                                    }}
                                                                />
                                                            </MuiMaterials.Box>
                                                            <MuiMaterials.Typography component="span" sx={{ fontSize: 12, color: themeText, whiteSpace: "nowrap" }}>
                                                                {requested > 0 ? `${finished}/${requested}` : "—"}
                                                            </MuiMaterials.Typography>
                                                        </MuiMaterials.Box>
                                                    </MuiMaterials.TableCell>
                                                    <MuiMaterials.TableCell sx={{ color: themeText, fontSize: "13px" }}>
                                                        {formatScanCable(row)}
                                                    </MuiMaterials.TableCell>
                                                    <MuiMaterials.TableCell sx={{ color: muted, fontSize: "13px" }}>
                                                        {row.version || "—"}
                                                    </MuiMaterials.TableCell>
                                                    <MuiMaterials.TableCell
                                                        align="right"
                                                        sx={{ width: 72, p: "4px 8px" }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <MuiMaterials.Tooltip title="Delete this scan" arrow>
                                                            <span>
                                                                <MuiMaterials.IconButton
                                                                    size="small"
                                                                    aria-label={`Delete scan for ${row.siteName || "site"}`}
                                                                    disabled={deleting}
                                                                    onClick={(e) => requestDeleteScan(row, e)}
                                                                    sx={{ color: themeText }}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </MuiMaterials.IconButton>
                                                            </span>
                                                        </MuiMaterials.Tooltip>
                                                    </MuiMaterials.TableCell>
                                                </MuiMaterials.TableRow>
                                            );
                                        })}
                                    </MuiMaterials.TableBody>
                                </MuiMaterials.Table>
                            </MuiMaterials.TableContainer>
                        )}
                    </ModalContentTile>
                </ModalBody>
            </ModalChrome>
            <ConfirmDialog
                open={!!deleteTarget}
                onClose={closeDeleteConfirm}
                title="Delete this scan?"
                titleId="delete-direct-scan-title"
                message={
                    deleteTarget
                        ? `This deletes the scan for ${deleteTarget.siteName || "this site"}${deleteTarget.type ? ` (${deleteTarget.type})` : ""}. This cannot be undone.`
                        : ""
                }
                actions={(
                    <>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            disabled={deleting}
                            onClick={closeDeleteConfirm}
                            sx={outlinedActionButtonSx()}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="contained"
                            disabled={deleting}
                            onClick={confirmDeleteScan}
                            sx={containedActionButtonSx()}
                        >
                            {deleting ? "Deleting…" : "Delete"}
                        </MuiMaterials.Button>
                    </>
                )}
            />
        </MuiMaterials.ThemeProvider>
    );
};

export default SitesScanningModal;
